/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getRouteInitData, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SelectionModel, emptySelectionModel, selectionModelAfterRowRemoved } from '@gh/core-data';
import { FocusedCell, GridComponent } from '@gh/core-ui-grid';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, map$, switchMap$, take$, takeUntil$ } from '@gh/rx/operators';
import { Observable, Subject } from 'rxjs';
import { getHpeComponentId, HpeComponent } from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import {
  HpeDetailsState,
  HpeFocusedTable,
  queryHpeComponents,
  queryHpeEditable,
  queryHpeFocusedCell,
  queryHpeSelectedComponentId,
} from '../hpe-details.state';
import { HpeDetailsComponentGridDefinition } from './hpe-details-component-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-hpe-details-component-list',
  templateUrl: 'hpe-details-component-list.component.html',
  styleUrls: ['hpe-details-component-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HpeDetailsComponentGridDefinition],
})
export class HpeDetailsComponentListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('grid') grid: GridComponent;

  private readonly destroy$ = new Subject<void>();

  selectedComponentId$: Observable<Maybe<ID>>;
  components$: Observable<HpeComponent[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  editable: boolean = true;
  sourceSelectionModel = emptySelectionModel();

  constructor(private route: ActivatedRoute,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService,
              public gridDefinition: HpeDetailsComponentGridDefinition,
              private cd: ChangeDetectorRef,
              private dialogService: DialogService) {

  }

  ngOnInit(): void {
    const { hpeComponentGridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(hpeComponentGridLayout);

    this.components$ = this.state.select(queryHpeComponents);
    this.selectedComponentId$ = this.state.select(queryHpeSelectedComponentId);
    this.focusedCell$ = this.state.select(queryHpeFocusedCell, HpeFocusedTable.ComponentList);
    this.editable = this.state.project(queryHpeEditable);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.api?.sizeColumnsToFit();
        window.dispatchEvent(new Event('resize'));
      }
      this.cd.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onAdd(): void {
    this.commands.componentAdd();
  }

  onHpeCloneComponent(component: HpeComponent): void {
    this.commands.hpeComponentClone(component);
  }
  onDelete(component: HpeComponent): void {
    const id = getHpeComponentId(component);
    this.commands.componentRemove(component).pipe(
      takeUntil$(this.destroy$),
    ).subscribe(() => {
      const components = this.state.project(queryHpeComponents) || [];
      const next = selectionModelAfterRowRemoved(
        this.sourceSelectionModel,
        id,
        components.length === 0,
      );
      if (next !== this.sourceSelectionModel) {
        this.onComponentSelectionChanged(next);
      }
    });
  }

  onSelectedComponentChanged(id?: ID): void {
    this.commands.componentSelect(id);
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  onComponentSelectionChanged(model: SelectionModel): void {
    this.sourceSelectionModel = model;
  }

  getSelectedComponents(): Observable<HpeComponent[]> {
    return this.components$.pipe(
      map$((components) => {
        if (!components || components.length === 0) {
          return [];
        }

        if (this.sourceSelectionModel.isExcludeMode) {
          return components.filter((component) => !this.sourceSelectionModel.idList.includes(getHpeComponentId(component)));
        } else {
          return components.filter((component) => this.sourceSelectionModel.idList.includes(getHpeComponentId(component)));
        }
      }));
  }

  hasSelectedComponents(): Observable<boolean> {
    return this.getSelectedComponents().pipe(
      map$((selectedComponents) => selectedComponents.length > 0));
  }

  onDeleteSelected(): void {
    this.getSelectedComponents().pipe(
      take$(1),
      filter$((selectedComponents) => selectedComponents.length > 0),
      switchMap$((selectedComponents) => {
        const count = selectedComponents.length;
        const confirmConfig = count === 1
          ? yesNoConfirmation('msg_bulk_remove_single_component', { code: selectedComponents[0].code })
          : yesNoConfirmation('msg_bulk_remove_n_components', { count: String(count) });
        return this.dialogService.confirmation(confirmConfig).pipe(
          filter$(yesAnswer),
          map$(() => selectedComponents));
      }),
    ).subscribe((selectedComponents) => {
      this.commands.removeComponents(selectedComponents.map((component) => getHpeComponentId(component)));
      this.sourceSelectionModel = emptySelectionModel();
      this.onComponentSelectionChanged(this.sourceSelectionModel);
    });
  }
}
